// ============================================================================
// PITCH SELECTION ENGINE — selectPitchContent.js
// ============================================================================
// Pure module: no React, no Supabase. Given an inquiry and the comms content,
// it proposes a deck: which pitch type fits, which projects go in which
// chapter, which images carry each project, and why. Every choice comes with
// a reason string the builder shows the reviewer, and every exclusion is kept
// in the list as include: false rather than silently dropped.
//
// See docs/pitch-deck-generator-plan.md section 4.
//
// Inputs are plain rows:
//   inquiry   public.inquiries (camelCase or snake_case both read)
//   client    public.clients row or null
//   types     comms.pitch_types rows
//   projects  merged projects from CommsPage (project_code, name, typology,
//             status, priority, confidential, headline, website_text)
//   ranks     comms.asset_ranks rows      tags   comms.asset_tags rows
//   awards    comms.awards rows           publications comms.publications rows
//   themeAssets comms.theme_assets rows
// ============================================================================

import { PITCH_TAGS, feeBandFor, readChapters, readRules, readSections, newId } from './pitchSections';

// Inquiry project types (free text, see BRIO_PROJECT_TYPES on the Inquiries
// page) onto public.projects.category. Reviewed with the founder before
// phase 3; unknown types fall through unmapped and match only the general
// profile.
export const INQUIRY_TYPE_TO_CATEGORY = {
  houses: 'houses',
  house: 'houses',
  residential: 'houses',
  villa: 'houses',
  housing: 'housing',
  redevelopment: 'housing',
  hospitality: 'hospitality',
  resort: 'hospitality',
  hotel: 'hospitality',
  interior: 'interior',
  interiors: 'interior',
  institutional: 'institutional',
  educational: 'educational',
  education: 'educational',
  school: 'educational',
  cultural: 'cultural',
  commercial: 'commercial',
  offices: 'commercial',
  office: 'commercial',
  retail: 'commercial',
  industrial: 'commercial',
  ngo: 'ngo',
  public: 'public',
  masterplan: 'public',
  landscape: 'public',
  transportation: 'public',
};

export const DEFAULT_EUR_INR = 90;

const norm = s => (s == null ? '' : String(s)).trim().toLowerCase();

// "Housing, Masterplan" → first recognised type wins.
export function categoryForInquiryType(projectType) {
  const parts = norm(projectType).split(/[,/&]+/).map(p => p.trim()).filter(Boolean);
  for (const p of parts) {
    if (INQUIRY_TYPE_TO_CATEGORY[p]) return INQUIRY_TYPE_TO_CATEGORY[p];
  }
  return parts[0] || '';
}

function field(row, camel, snake) {
  if (!row) return undefined;
  if (row[camel] !== undefined) return row[camel];
  return row[snake];
}

// The facts about an inquiry the rules are matched against.
export function inquiryProfile(inquiry, client, eurInrRate = DEFAULT_EUR_INR) {
  const feeInr = Number(field(inquiry, 'estimatedFee', 'estimated_fee'));
  const rate = Number(eurInrRate) > 0 ? Number(eurInrRate) : DEFAULT_EUR_INR;
  const feeEur = Number.isFinite(feeInr) && feeInr > 0 ? feeInr / rate : null;
  const plot = Number(field(inquiry, 'plotArea', 'plot_area'));
  const clientName =
    field(inquiry, 'clientOrgNameOverride', 'client_org_name_override') ||
    field(inquiry, 'clientNameOverride', 'client_name_override') ||
    (client && (client.display_name || client.name)) || '';
  return {
    projectType: field(inquiry, 'projectType', 'project_type') || '',
    category: categoryForInquiryType(field(inquiry, 'projectType', 'project_type')),
    feeInr: Number.isFinite(feeInr) && feeInr > 0 ? feeInr : null,
    feeEur,
    feeBand: feeBandFor(feeEur),
    plotArea: Number.isFinite(plot) && plot > 0 ? plot : null,
    clientType: client ? client.client_type || null : null,
    clientName,
    projectName: field(inquiry, 'projectName', 'project_name') || '',
    city: field(inquiry, 'locationCity', 'location_city') || '',
    briefNotes: field(inquiry, 'briefNotes', 'brief_notes') || '',
  };
}

// ── Pitch type ───────────────────────────────────────────────────────────────

// A rule that is set and matches adds; a rule that is set and misses subtracts
// more than it could add, so a type with a wrong category never beats the
// general profile. Empty rules are neutral.
export function scorePitchType(type, profile) {
  const rules = readRules(type.rules);
  let score = 0;
  const reasons = [];
  if (rules.categories.length) {
    if (profile.category && rules.categories.includes(profile.category)) { score += 3; reasons.push(`category ${profile.category}`); }
    else { score -= 4; reasons.push(`category ${profile.category || 'unknown'} not in ${rules.categories.join(', ')}`); }
  }
  if (rules.fee_bands.length) {
    if (profile.feeBand && rules.fee_bands.includes(profile.feeBand)) { score += 2; reasons.push(`fee band ${profile.feeBand}`); }
    else if (profile.feeBand) { score -= 3; reasons.push(`fee band ${profile.feeBand} not in ${rules.fee_bands.join(', ')}`); }
    else reasons.push('no estimated fee to match');
  }
  if (rules.client_types.length) {
    if (profile.clientType && rules.client_types.includes(profile.clientType)) { score += 1; reasons.push(`client type ${profile.clientType}`); }
    else if (profile.clientType) { score -= 2; reasons.push(`client type ${profile.clientType} not in ${rules.client_types.join(', ')}`); }
    else reasons.push('client type unknown');
  }
  if (rules.plot_min != null || rules.plot_max != null) {
    const p = profile.plotArea;
    if (p != null && (rules.plot_min == null || p >= rules.plot_min) && (rules.plot_max == null || p <= rules.plot_max)) { score += 1; reasons.push(`plot ${p} m² in range`); }
    else if (p != null) { score -= 2; reasons.push(`plot ${p} m² out of range`); }
    else reasons.push('no plot area to match');
  }
  if (!reasons.length) reasons.push('no rules, matches any inquiry');
  return { score, reasons };
}

export function rankPitchTypes(types, profile) {
  return (types || [])
    .filter(t => t.active !== false)
    .map(t => ({ type: t, ...scorePitchType(t, profile) }))
    .sort((a, b) => b.score - a.score || (a.type.sort_order || 0) - (b.type.sort_order || 0));
}

// ── Images ───────────────────────────────────────────────────────────────────

// A deck is built entirely from Dropbox thumbnails, so a format Dropbox will
// not render is a blank frame on the page. Every one of the 111 TIFFs in the
// press library came back without a thumbnail, while every PNG, HEIC, GIF and
// JPEG got one, so TIFF is out. The test is the extension, not a missing
// thumb_url: a file nobody has opened in the press browser yet has no
// thumb_url either, and it renders perfectly well once asked for.
export const THUMBNAILABLE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic']);
const extOf = name => String(name || '').split('.').pop().toLowerCase();
const canRender = a => THUMBNAILABLE_EXTS.has(extOf(a.file_name));

// The same photograph is filed at several resolutions under the same name, and
// is often starred in more than one of those folders. Preference order for
// which copy the deck uses; anything else sorts last, since most other folders
// (Renders, Construction Process) hold their own pictures rather than copies.
export const FOLDER_PREFERENCE = ['jpeg-highres', 'jpeg-midres', 'jpeg-lowres', 'jpeg-web'];
const folderRank = path => {
  const parts = String(path || '').split('/');
  const i = FOLDER_PREFERENCE.indexOf(String(parts[parts.length - 2] || '').toLowerCase());
  return i === -1 ? FOLDER_PREFERENCE.length : i;
};
// Copies of one photograph share a file name; the extension is dropped so a
// TIFF and its JPEG twin count as the same picture.
const photoKey = name => String(name || '').replace(/\.[^.]+$/, '').trim().toLowerCase();

// One record per photograph for a project, merging rank and tag rows across
// every copy of it. Stars and tags belong to the photograph rather than to the
// file somebody happened to be looking at, so they are pooled; the file the
// deck uses is the best copy that can actually be rendered. A photograph whose
// only copies are TIFFs is left out entirely, because the alternative is a
// blank frame where a picture should be.
export function projectAssets(projectCode, ranks, tags) {
  const byId = new Map();
  const take = row => {
    if (!row || !row.dropbox_file_id || row.project_code !== projectCode) return;
    const cur = byId.get(row.dropbox_file_id) || { file_id: row.dropbox_file_id, path: row.dropbox_path || '', file_name: row.file_name || '', thumb_url: null, is_video: false, rank: 0, tags: [] };
    if (row.thumb_url && !cur.thumb_url) cur.thumb_url = row.thumb_url;
    if (row.is_video) cur.is_video = true;
    if (row.rank != null) cur.rank = Math.max(cur.rank, Number(row.rank) || 0);
    if (Array.isArray(row.tags)) cur.tags = Array.from(new Set([...cur.tags, ...row.tags]));
    byId.set(row.dropbox_file_id, cur);
  };
  (ranks || []).forEach(take);
  (tags || []).forEach(take);

  const byPhoto = new Map();
  Array.from(byId.values()).filter(a => !a.is_video).forEach(a => {
    const key = photoKey(a.file_name);
    const cur = byPhoto.get(key);
    if (!cur) { byPhoto.set(key, { ...a, alias_ids: [] }); return; }
    // The ids of the copies that lost, so a deck that already stored one of
    // them still recognises its own choice.
    cur.alias_ids = Array.from(new Set([...cur.alias_ids, cur.file_id, a.file_id])).filter(id => id !== cur.file_id);
    cur.rank = Math.max(cur.rank, a.rank);
    cur.tags = Array.from(new Set([...cur.tags, ...a.tags]));
    // The representative is the best renderable copy. A renderable copy always
    // beats one that is not, whatever folder each sits in.
    const better = canRender(a) && (!canRender(cur) || folderRank(a.path) < folderRank(cur.path));
    if (better) {
      cur.alias_ids = Array.from(new Set([...cur.alias_ids, cur.file_id])).filter(id => id !== a.file_id);
      Object.assign(cur, { file_id: a.file_id, path: a.path, file_name: a.file_name, thumb_url: a.thumb_url || cur.thumb_url });
    }
    // Any copy's thumbnail will do for the picker's grid: it is the same
    // photograph, and only the preview tile reads this. Without it, choosing
    // the highres copy would empty tiles that used to show something.
    else if (!cur.thumb_url) cur.thumb_url = a.thumb_url;
  });
  return Array.from(byPhoto.values()).filter(canRender).map(a => ({ ...a, alias_ids: a.alias_ids || [] }));
}

// Any copy's id onto the id the deck should use, so a choice stored before a
// photograph's copies were collapsed still points at a picture.
export function resolveAssetId(assets, id) {
  if (!id) return id;
  const match = (assets || []).find(a => a.file_id === id || (a.alias_ids || []).includes(id));
  return match ? match.file_id : id;
}

const hasTag = (a, t) => a.tags.includes(t);

// Hero: the pitch-hero tag, highest rank first. Supporting: pitch tag by rank,
// then five star, then four star, never the hero twice.
export function pickImages(assets, imagesPerProject) {
  const byRank = (a, b) => b.rank - a.rank || a.file_name.localeCompare(b.file_name);
  const heroes = assets.filter(a => hasTag(a, PITCH_TAGS.hero)).sort(byRank);
  const hero = heroes[0] || null;
  const reasons = [];
  if (hero) reasons.push(heroes.length > 1 ? `hero: ${heroes.length} tagged, highest rank used` : 'hero tagged');
  const pool = assets.filter(a => !hero || a.file_id !== hero.file_id);
  const tagged = pool.filter(a => hasTag(a, PITCH_TAGS.supporting)).sort(byRank);
  const five = pool.filter(a => !hasTag(a, PITCH_TAGS.supporting) && a.rank >= 5).sort(byRank);
  const four = pool.filter(a => !hasTag(a, PITCH_TAGS.supporting) && a.rank === 4).sort(byRank);
  const n = Math.max(0, Number(imagesPerProject) || 0);
  const supporting = [...tagged, ...five, ...four].slice(0, n);
  if (tagged.length) reasons.push(`${Math.min(tagged.length, n)} tagged supporting`);
  if (supporting.length > tagged.length) reasons.push(`${supporting.length - Math.min(tagged.length, n)} filled from 4 and 5 star`);
  // With no hero tag, the best ranked image stands in so the spread is not blank,
  // and the reason says so.
  let heroId = hero ? hero.file_id : null;
  let heroFallback = false;
  if (!heroId) {
    const best = [...pool].sort(byRank)[0];
    if (best && best.rank >= 4) { heroId = best.file_id; heroFallback = true; reasons.push(`no hero tag, ${best.rank} star image used`); }
    else reasons.push('no hero tag and no 4 or 5 star image');
  }
  return { hero_file_id: heroId, hero_fallback: heroFallback, image_file_ids: supporting.filter(a => a.file_id !== heroId).map(a => a.file_id), reasons };
}

// ── Projects ─────────────────────────────────────────────────────────────────

const awardsFor = (code, awards) => (awards || []).filter(a => Array.isArray(a.project_codes) && a.project_codes.includes(code));
const publicationsFor = (code, publications) => (publications || []).filter(p => Array.isArray(p.project_codes) && p.project_codes.includes(code));

export function scoreProject(project, ctx) {
  const { category, awards, assets, allowConfidential } = ctx;
  const reasons = [];
  const blockers = [];
  let score = 0;
  const code = project.project_code;
  if (category && norm(project.typology) === norm(category)) { score += 3; reasons.push('category match'); }
  if (project.priority) { score += 2; reasons.push('starred'); }
  const n = awardsFor(code, awards).length;
  if (n) { score += Math.min(n, 3); reasons.push(`${n} award${n === 1 ? '' : 's'}`); }
  const heroTagged = assets.some(a => hasTag(a, PITCH_TAGS.hero));
  if (heroTagged) { score += 2; reasons.push('hero tagged'); }
  const five = assets.filter(a => a.rank >= 5).length;
  if (five) { score += Math.min(five, 5) * 0.5; reasons.push(`${five} five star`); }
  if (norm(project.status) === 'completed') { score += 1; reasons.push('completed'); }

  if (project.confidential && !allowConfidential) blockers.push('confidential, override needed');
  const hasText = !!(project.website_text && project.website_text.trim()) || !!(project.headline && project.headline.trim());
  if (!hasText) blockers.push('no text');
  if (!heroTagged && !assets.some(a => a.rank >= 4)) blockers.push('no hero image');
  return { score, reasons, blockers };
}

// ── Draft deck ───────────────────────────────────────────────────────────────

// `allowConfidential` is the deck's own setting, not a property of the
// project. When it is on, scoreProject has already dropped the confidential
// blocker, so the entry has to carry the override too: without it the builder
// locks the include checkbox (lockedByNda) on the very project the deck was
// told to allow.
function chapterProjectEntry(project, images, scored, allowConfidential) {
  const reason = [...scored.reasons, ...images.reasons].join(', ');
  return {
    project_code: project.project_code,
    include: scored.blockers.length === 0,
    confidential: !!project.confidential,
    confidential_override: !!allowConfidential && !!project.confidential,
    hero_file_id: images.hero_file_id,
    image_file_ids: images.image_file_ids,
    headline_override: null,
    text_override: null,
    reason: scored.blockers.length ? `${scored.blockers.join(', ')} (${reason})` : reason,
    blockers: scored.blockers,
  };
}

export function buildDraftDeck({ inquiry, client, profile: givenProfile, pitchType, projects, ranks, tags, awards, publications, themes, themeAssets, allowConfidential = false, eurInrRate }) {
  const profile = givenProfile || inquiryProfile(inquiry, client, eurInrRate);
  const type = pitchType;
  const typeChapters = readChapters(type.chapters);
  const perChapter = Math.max(1, Number(type.projects_per_chapter) || 4);
  const perProject = Number(type.images_per_project);
  const imagesPerProject = Number.isFinite(perProject) ? perProject : 6;
  const reasons = [];

  // Chapters: copy, then move the inquiry's category to the front.
  let chapters = typeChapters.length
    ? typeChapters.map(c => ({ ...c }))
    : [{ id: newId('ch'), category: profile.category || '', label: profile.category ? cap(profile.category) : 'Selected projects', project_order: [] }];
  if (profile.category) {
    const idx = chapters.findIndex(c => norm(c.category) === norm(profile.category));
    if (idx > 0) { const [ch] = chapters.splice(idx, 1); chapters.unshift(ch); reasons.push(`${ch.label || ch.category} chapter moved first for a ${profile.category} inquiry`); }
    if (idx === -1 && typeChapters.length) reasons.push(`no ${profile.category} chapter in this pitch type`);
  }

  const byCode = new Map((projects || []).map(p => [p.project_code, p]));
  const used = new Set();
  const scoreCache = new Map();
  const evaluate = project => {
    if (!scoreCache.has(project.project_code)) {
      const assets = projectAssets(project.project_code, ranks, tags);
      const scored = scoreProject(project, { category: profile.category, awards, assets, allowConfidential });
      const images = pickImages(assets, imagesPerProject);
      scoreCache.set(project.project_code, { scored, images });
    }
    return scoreCache.get(project.project_code);
  };

  const outChapters = chapters.map(ch => {
    const entries = [];
    // 1. the default order, kept whole so the reviewer sees the intended list
    ch.project_order.forEach(code => {
      const p = byCode.get(code);
      if (!p || used.has(code)) return;
      const { scored, images } = evaluate(p);
      const entry = chapterProjectEntry(p, images, scored, allowConfidential);
      entries.push({ ...entry, reason: `in default order; ${entry.reason}` });
      used.add(code);
    });
    // 2. fill the remaining slots from the category, best score first
    const included = () => entries.filter(e => e.include).length;
    if (included() < perChapter && ch.category) {
      const candidates = (projects || [])
        .filter(p => p.project_code && !used.has(p.project_code) && norm(p.typology) === norm(ch.category))
        .map(p => ({ p, ...evaluate(p) }))
        .sort((a, b) => b.scored.score - a.scored.score || a.p.project_code.localeCompare(b.p.project_code));
      for (const c of candidates) {
        if (included() >= perChapter) break;
        const entry = chapterProjectEntry(c.p, c.images, c.scored, allowConfidential);
        entries.push({ ...entry, reason: `by score; ${entry.reason}` });
        used.add(c.p.project_code);
      }
    }
    return { id: ch.id, category: ch.category, label: ch.label || cap(ch.category), projects: entries };
  });

  // Sections: enabled ones only, with a flag where the content behind them is empty.
  const themeCounts = {};
  (themeAssets || []).forEach(t => { themeCounts[t.theme_id] = (themeCounts[t.theme_id] || 0) + 1; });
  const themesById = Object.fromEntries((themes || []).map(t => [t.comms_id, t]));
  const selectedCodes = outChapters.flatMap(c => c.projects.filter(p => p.include).map(p => p.project_code));
  const sections = readSections(type.sections).filter(s => s.enabled).map(s => {
    let flag = null;
    if (s.key === 'theme') {
      const id = s.settings.theme_id;
      if (!id) flag = 'no theme chosen';
      else if (!themesById[id]) flag = 'theme no longer exists';
      else if (!themeCounts[id]) flag = `${themesById[id].label}: no highlight images yet`;
    }
    if (s.key === 'awards' && s.settings.scope !== 'all' && !selectedCodes.some(c => awardsFor(c, awards).length)) flag = 'no awards linked to the selected projects';
    if (s.key === 'publications' && s.settings.scope !== 'all' && !selectedCodes.some(c => publicationsFor(c, publications).length)) flag = 'no publications linked to the selected projects';
    return { ...s, flag };
  });

  const title = profile.projectName ? `${profile.projectName}` : (profile.clientName ? `Pitch for ${profile.clientName}` : type.label);
  return {
    title,
    client_name: profile.clientName || null,
    pitch_type_id: type.comms_id,
    format: type.format || 'screen',
    quality: 'email',
    status: 'draft',
    sections,
    chapters: outChapters,
    intro_text: type.intro_template || '',
    allow_confidential: !!allowConfidential,
    reasons,
    profile,
  };
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
